import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import {
  FileInput, Receipt, BookOpen, ArrowLeftRight, GitBranch, FileDown,
  CheckCircle2, ChevronRight, BookText, Sparkles, Shield, Zap,
  Users, Calculator, Wifi, Link2, Star, ArrowRight, Coins,
  Building2, PenLine, BarChart3, Lock, Wallet, Briefcase,
  Mail, Bot, CreditCard, Scale, Globe, Layers
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULES = [
  { icon: FileInput, title: 'Factures fournisseurs', desc: 'Déposez un PDF ou une image — l\'IA extrait fournisseur, montants, lignes et RIB en 5 secondes.', category: 'achats' },
  { icon: Receipt, title: 'Facturation client', desc: 'Créez, envoyez par email et suivez vos factures. Relances automatiques sur les impayés.', category: 'ventes' },
  { icon: Users, title: 'Gestion clients & fournisseurs', desc: 'Répertoire enrichi via INSEE avec autocomplétion SIRET, TVA intracommunautaire calculée.', category: 'achats' },
  { icon: BookOpen, title: 'Plan comptable PCG', desc: '95 comptes pré-configurés. Personnalisez, ajoutez vos comptes, respectez le PCG.', category: 'compta' },
  { icon: PenLine, title: 'Écritures comptables', desc: 'Saisie débit/crédit avec vérification d\'équilibre en temps réel. Validation en masse.', category: 'compta' },
  { icon: BookText, title: 'Grand livre & Balance', desc: 'Vue par compte avec tous les mouvements. Balance avec contrôle d\'équilibre automatique.', category: 'compta' },
  { icon: FileDown, title: 'Export FEC', desc: 'Fichier des Écritures Comptables conforme A.47 A-1 du LPF. Validation pré-export.', category: 'compta' },
  { icon: ArrowLeftRight, title: 'Rapprochement bancaire', desc: 'Import CSV de relevés, interface de rapprochement écriture ↔ mouvement avec progression.', category: 'banque' },
  { icon: Wifi, title: 'Connexion bancaire', desc: 'Synchronisation automatique via Bridge ou GoCardless. Catégorisation intelligente.', category: 'banque' },
  { icon: Link2, title: 'Lettrage automatique', desc: 'Algorithme de matching avec score de confiance (montant, référence, date). Validation en masse.', category: 'banque' },
  { icon: Calculator, title: 'TVA automatique', desc: 'Calcul collectée/déductible par taux (20%, 10%, 5.5%, 2.1%). Déclarations CA3. Alertes deadline.', category: 'compta' },
  { icon: GitBranch, title: 'Workflows de validation', desc: 'Circuits d\'approbation multi-niveaux configurables. Notifications email automatiques.', category: 'pilotage' },
  { icon: Mail, title: 'Collecte email', desc: 'Boîte email dédiée — les factures reçues sont lues par IA et créées en brouillon automatiquement.', category: 'auto' },
  { icon: BarChart3, title: 'Rapports & Analytique', desc: 'Dashboard, dépenses par fournisseur/catégorie, aging report, comptabilité analytique.', category: 'pilotage' },
  { icon: Receipt, title: 'Notes de frais', desc: 'Saisie avec OCR ticket, barème kilométrique fiscal, workflow de validation, remboursement.', category: 'pilotage' },
  { icon: Wallet, title: 'Trésorerie prévisionnelle', desc: 'Cash-flow à 30/60/90 jours. 3 scénarios. Charges récurrentes auto-détectées. Alertes.', category: 'banque' },
  { icon: Briefcase, title: 'Espace expert-comptable', desc: 'Accès multi-structure, dashboard cabinet, chat par dossier, export Sage/Cegid/ACD.', category: 'expert' },
  { icon: Coins, title: 'Multi-devises', desc: 'Taux BCE quotidiens, convertisseur, écarts de change automatiques.', category: 'compta' },
  { icon: Shield, title: 'Rôles & Permissions', desc: 'Rôles personnalisés avec permissions granulaires par module. Contrôle d\'accès fin.', category: 'admin' },
  { icon: Bot, title: 'IA configurable', desc: 'Claude ou GPT-4 au choix. Configuration par structure. Provider switchable à tout moment.', category: 'auto' },
];

const CATEGORIES = [
  { key: 'achats', label: 'Achats', color: '#0F5156' },
  { key: 'ventes', label: 'Ventes', color: '#C27868' },
  { key: 'compta', label: 'Comptabilité', color: '#4A90A4' },
  { key: 'banque', label: 'Banque', color: '#69AFAF' },
  { key: 'pilotage', label: 'Pilotage', color: '#6366f1' },
  { key: 'auto', label: 'Automatisation', color: '#f59e0b' },
  { key: 'expert', label: 'Expert-comptable', color: '#8b5cf6' },
  { key: 'admin', label: 'Administration', color: '#64748b' },
];

const STEPS = [
  { num: '01', title: 'Déposez', desc: 'PDF, image ou email — notre IA fait le reste en 5 secondes', icon: FileInput },
  { num: '02', title: 'L\'IA extrait', desc: 'Fournisseur, montants, lignes, RIB — enrichi via INSEE', icon: Sparkles },
  { num: '03', title: 'Validez', desc: 'Workflow configurable, notifications automatiques', icon: CheckCircle2 },
  { num: '04', title: 'Comptabilisez', desc: 'Écritures auto, rapprochement bancaire, export FEC', icon: BookOpen },
];

const STATS = [
  { value: '5s', label: 'Lecture IA' },
  { value: '95%', label: 'Précision OCR' },
  { value: '20+', label: 'Modules' },
  { value: '100%', label: 'FEC conforme' },
];

const FAQ = [
  { q: 'Combien de temps dure l\'essai gratuit ?', a: '7 jours d\'essai gratuit avec accès à toutes les fonctionnalités. Sans carte bancaire. Sans engagement.' },
  { q: 'Nexus Compta est-il conforme ?', a: 'Oui. Export FEC conforme A.47 A-1 du LPF, plan comptable PCG, mentions légales obligatoires sur les factures, piste d\'audit.' },
  { q: 'Comment fonctionne la lecture IA ?', a: 'Déposez un PDF ou une image. Notre IA (Claude ou GPT-4 au choix) extrait le fournisseur, montants, lignes et RIB. Les données sont enrichies via le répertoire INSEE.' },
  { q: 'Puis-je connecter ma banque ?', a: 'Oui. Bridge (spécialiste FR) ou GoCardless (Open Banking EU). Transactions synchronisées automatiquement, lettrage intelligent proposé.' },
  { q: 'Mon expert-comptable peut-il accéder ?', a: 'Oui. Espace dédié avec accès multi-structure, chat par dossier, demandes de pièces, export vers Sage/Cegid/ACD.' },
  { q: 'Quels moyens de paiement ?', a: 'Carte bancaire ou prélèvement SEPA. Facturation automatique avec PDF envoyé par email à chaque paiement.' },
];

export default function Landing() {
  const [annual, setAnnual] = useState(false);
  const [packages, setPackages] = useState([]);
  const [activeCat, setActiveCat] = useState('all');

  useEffect(() => {
    axios.get(`${API_URL}/public/packages`).then(r => setPackages(r.data)).catch(() => {});
  }, []);

  const filteredModules = activeCat === 'all' ? MODULES : MODULES.filter(m => m.category === activeCat);

  return (
    <div className="min-h-screen bg-[#fafbfc]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0F5156] to-[#0d464a] flex items-center justify-center shadow-lg shadow-[#0F5156]/20">
              <BookText className="w-4.5 h-4.5 text-white" />
            </div>
            <span className="font-bold text-[17px] text-slate-900 tracking-tight">Nexus Compta</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-500">
            <a href="#modules" className="hover:text-[#0F5156] transition-colors">Modules</a>
            <a href="#pricing" className="hover:text-[#0F5156] transition-colors">Tarifs</a>
            <a href="#faq" className="hover:text-[#0F5156] transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" asChild className="text-sm"><Link to="/login">Connexion</Link></Button>
            <Button asChild className="bg-gradient-to-r from-[#0F5156] to-[#0d464a] hover:from-[#0d464a] hover:to-[#0a3538] text-sm shadow-lg shadow-[#0F5156]/20">
              <Link to="/login?tab=register">Essai gratuit</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-20 sm:pt-28 pb-12 overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#0F5156]/5 rounded-full blur-[120px]" />
        <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-[#69AFAF]/5 rounded-full blur-[100px]" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <Badge className="mb-6 bg-[#0F5156]/10 text-[#0F5156] border-[#0F5156]/20 py-1.5 px-5 text-sm font-medium">
            <Sparkles className="w-4 h-4 mr-2" /> 20+ modules — Lecture IA intégrée
          </Badge>
          <h1 className="text-5xl sm:text-[64px] font-black text-slate-900 tracking-tight leading-[1.05]">
            La comptabilité
            <br />
            <span className="bg-gradient-to-r from-[#0F5156] to-[#69AFAF] bg-clip-text text-transparent">
              du futur
            </span>
            , aujourd'hui.
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Factures IA, écritures automatiques, connexion bancaire, TVA, trésorerie prévisionnelle — tout en une plateforme.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" asChild className="bg-gradient-to-r from-[#0F5156] to-[#0d464a] hover:from-[#0d464a] hover:to-[#0a3538] h-14 px-10 text-base shadow-xl shadow-[#0F5156]/25 w-full sm:w-auto">
              <Link to="/login?tab=register">Démarrer gratuitement <ChevronRight className="w-5 h-5 ml-2" /></Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="h-14 px-10 text-base w-full sm:w-auto border-slate-200">
              <Link to="/login">Se connecter</Link>
            </Button>
          </div>
          <p className="mt-5 text-sm text-slate-400">7 jours gratuits — Aucune carte bancaire — Annulation en 1 clic</p>
        </div>

        {/* Video */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-16">
          <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_100px_-20px_rgba(15,81,86,0.25)] border border-slate-200/60 bg-white">
            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none z-10 rounded-2xl" />
            <video autoPlay muted loop playsInline className="w-full"><source src="/presentation.mp4" type="video/mp4" /></video>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 border-y border-slate-100 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-[#0F5156] to-[#69AFAF] bg-clip-text text-transparent">{s.value}</p>
                <p className="text-sm text-slate-500 mt-1 font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-[#0F5156]/10 text-[#0F5156] border-0">Comment ça marche</Badge>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">4 étapes, zéro effort</h2>
          </div>
          <div className="grid sm:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <div key={i} className="relative group text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0F5156] to-[#69AFAF] flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#0F5156]/20 group-hover:scale-110 transition-transform">
                  <step.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <span className="text-xs font-bold text-[#0F5156]/30 absolute top-0 right-1/4">{step.num}</span>
                <h3 className="text-base font-bold text-slate-900 mb-1">{step.title}</h3>
                <p className="text-sm text-slate-500">{step.desc}</p>
                {i < 3 && <ArrowRight className="hidden sm:block absolute -right-4 top-7 w-5 h-5 text-slate-200" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Modules */}
      <section id="modules" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-[#0F5156]/10 text-[#0F5156] border-0">20+ modules</Badge>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Tout ce dont vous avez besoin</h2>
            <p className="mt-4 text-lg text-slate-500 max-w-xl mx-auto">Une plateforme complète qui remplace 5 outils différents.</p>
          </div>

          {/* Category filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <button onClick={() => setActiveCat('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCat === 'all' ? 'bg-[#0F5156] text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              Tout ({MODULES.length})
            </button>
            {CATEGORIES.map(cat => {
              const count = MODULES.filter(m => m.category === cat.key).length;
              return (
                <button key={cat.key} onClick={() => setActiveCat(cat.key)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCat === cat.key ? 'text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  style={activeCat === cat.key ? { background: cat.color } : {}}>
                  {cat.label} ({count})
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredModules.map((m, i) => {
              const cat = CATEGORIES.find(c => c.key === m.category);
              return (
                <Card key={i} className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-slate-100 bg-white overflow-hidden">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: `${cat?.color || '#0F5156'}15` }}>
                        <m.icon className="w-5 h-5" style={{ color: cat?.color || '#0F5156' }} strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 text-[13px] mb-1">{m.title}</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative bg-gradient-to-br from-[#0F5156] to-[#0a3538] rounded-3xl p-12 sm:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
            <div className="flex justify-center gap-1 mb-5">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />)}</div>
            <blockquote className="text-xl sm:text-2xl text-white font-medium leading-relaxed max-w-2xl mx-auto relative z-10">
              "Nexus Compta nous a fait gagner 2 jours par mois. La lecture IA est bluffante, le rapprochement bancaire est un game-changer."
            </blockquote>
            <div className="mt-6 flex items-center justify-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center text-white font-bold text-sm">ML</div>
              <div className="text-left"><p className="text-white font-medium text-sm">Marie Laurent</p><p className="text-teal-300 text-xs">DAF — TechVision SAS (47 salariés)</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing from API */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-[#0F5156]/10 text-[#0F5156] border-0">Tarifs</Badge>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Des prix transparents</h2>
            <p className="mt-4 text-slate-500">Tous les prix sont HT. TVA 20% applicable. Paiement CB ou SEPA.</p>
          </div>

          <div className="flex items-center justify-center gap-3 mb-10">
            <span className={`text-sm font-semibold ${!annual ? 'text-slate-900' : 'text-slate-400'}`}>Mensuel</span>
            <Switch checked={annual} onCheckedChange={setAnnual} />
            <span className={`text-sm font-semibold ${annual ? 'text-slate-900' : 'text-slate-400'}`}>Annuel</span>
            {annual && <Badge className="bg-green-100 text-green-700 text-xs font-bold ml-1">Économisez 17%</Badge>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.filter(p => p.is_active).map((pkg, i) => {
              const isMiddle = i === 1 || pkg.code === 'business';
              return (
                <Card key={pkg.id} className={`relative transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${isMiddle ? 'border-[#0F5156] border-2 shadow-xl scale-[1.03]' : 'border-slate-200'}`}>
                  {isMiddle && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-[#0F5156] to-[#69AFAF] text-white px-5 py-1.5 shadow-lg text-xs font-bold">Le plus populaire</Badge>
                    </div>
                  )}
                  <CardContent className="p-7 pt-10">
                    <h3 className="text-xl font-black text-slate-900">{pkg.name}</h3>
                    {pkg.description && <p className="text-sm text-slate-500 mt-1">{pkg.description}</p>}
                    <div className="mt-5 mb-7">
                      <span className="text-4xl font-black text-slate-900">{Math.round(annual ? pkg.price_annual_ht / 12 : pkg.price_monthly_ht)}€</span>
                      <span className="text-slate-400 text-sm"> /mois HT</span>
                      {annual && <p className="text-xs text-green-600 font-medium mt-1">Facturé {pkg.price_annual_ht}€ HT/an</p>}
                      <p className="text-xs text-slate-400 mt-0.5">soit {Math.round(annual ? pkg.price_annual_ttc / 12 : pkg.price_monthly_ttc)}€ TTC/mois</p>
                    </div>
                    <Button asChild className={`w-full h-12 font-semibold ${isMiddle ? 'bg-gradient-to-r from-[#0F5156] to-[#0d464a] shadow-lg' : ''}`} variant={isMiddle ? 'default' : 'outline'}>
                      <Link to="/login?tab=register">{isMiddle ? 'Commencer gratuitement' : 'Commencer'}</Link>
                    </Button>
                    <ul className="mt-7 space-y-2.5">
                      {pkg.modules?.map(m => (
                        <li key={m} className="flex items-center gap-2.5 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-[#0F5156] flex-shrink-0" />
                          <span className="text-slate-600">{pkg.modules_labels?.[m] || m}</span>
                        </li>
                      ))}
                      {pkg.max_invoices_monthly ? (
                        <li className="flex items-center gap-2.5 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-[#0F5156] flex-shrink-0" />
                          <span className="text-slate-600 font-semibold">{pkg.max_invoices_monthly} factures fournisseurs/mois</span>
                        </li>
                      ) : (
                        <li className="flex items-center gap-2.5 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-[#0F5156] flex-shrink-0" />
                          <span className="text-slate-600 font-semibold">Factures illimitées</span>
                        </li>
                      )}
                      {pkg.max_users && (
                        <li className="flex items-center gap-2.5 text-sm">
                          <Users className="w-4 h-4 text-[#0F5156]" />
                          <span className="text-slate-600">{pkg.max_users} utilisateurs</span>
                        </li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
            {packages.filter(p => p.is_active).length === 0 && (
              <div className="col-span-3 text-center py-12 text-slate-400">Chargement des tarifs...</div>
            )}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { icon: Shield, label: 'Hébergé en France' },
              { icon: FileDown, label: 'Export FEC conforme' },
              { icon: Lock, label: 'Chiffrement SSL' },
              { icon: Globe, label: 'Multi-devises BCE' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-3 p-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0F5156]/10 to-[#69AFAF]/10 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[#0F5156]" strokeWidth={1.5} />
                </div>
                <p className="text-sm font-semibold text-slate-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-[#0F5156]/10 text-[#0F5156] border-0">FAQ</Badge>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Questions fréquentes</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-100 rounded-2xl px-6 bg-[#fafbfc] shadow-sm">
                <AccordionTrigger className="text-[15px] font-bold text-slate-900 hover:no-underline py-5">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-500 leading-relaxed pb-5">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F5156] to-[#0a3538]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#69AFAF]/10 rounded-full blur-[150px]" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight">
            Prêt à passer à la<br />comptabilité du futur ?
          </h2>
          <p className="mt-5 text-lg text-teal-200/70">7 jours gratuits. 20+ modules. Aucune carte bancaire.</p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" asChild className="bg-white text-[#0F5156] hover:bg-slate-100 h-14 px-10 text-base font-bold shadow-2xl w-full sm:w-auto">
              <Link to="/login?tab=register">Démarrer gratuitement <ChevronRight className="w-5 h-5 ml-2" /></Link>
            </Button>
            <Button size="lg" variant="ghost" asChild className="text-white hover:bg-white/10 h-14 px-10 text-base w-full sm:w-auto border border-white/20">
              <Link to="/login">Se connecter</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-slate-900 text-slate-400">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <BookText className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-bold text-white">Nexus Compta</span>
            </div>
            <div className="flex items-center gap-8 text-xs">
              <span className="hover:text-white cursor-pointer">Mentions légales</span>
              <span className="hover:text-white cursor-pointer">Confidentialité</span>
              <span className="hover:text-white cursor-pointer">CGV</span>
              <span className="hover:text-white cursor-pointer">Contact</span>
            </div>
            <p className="text-xs">© {new Date().getFullYear()} Nexus Compta</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
